/**
 * Shared chip + marker-status palette for the dashboard surfaces.
 *
 * The MAP and OPS dashboards (and their map markers / popovers) all paint
 * alarm chips and connection-status dots from the same small palette. Holding
 * it in one place keeps the two dashboards visually in lockstep instead of
 * drifting copy-pasted color blocks.
 *
 * Contains:
 * - CHIP_STYLES        — tinted bg/text styles per chip color key
 * - severityChipStyle  — alarm severity -> chip style (neutral default fallback)
 * - MAP_STATUS_COLORS  — marker/dot colors for alarm / offline / ok states
 * - GRADIENTS          — stat-card gradient backgrounds keyed by tone
 */

// Tinted background + readable text per chip color key. The single definition
// shared by the ops-dashboard AlarmChip and the map-marker popover. Values
// resolve through the --status-* semantic tokens (theme/cssTokens.js).
export const CHIP_STYLES = {
  error: { bgcolor: 'var(--status-bad-tint)', color: 'var(--status-bad-text)' },
  warning: { bgcolor: 'var(--status-warning-tint)', color: 'var(--status-warning-text)' },
  attention: { bgcolor: 'var(--status-attention-tint)', color: 'var(--status-attention-text)' },
  info: { bgcolor: 'var(--status-info-tint)', color: 'var(--status-info-text)' },
  ok: { bgcolor: 'var(--status-ok-tint)', color: 'var(--status-ok-text)' },
  default: { bgcolor: 'var(--status-neutral-tint)', color: 'var(--status-neutral-text)' },
};

// Alarm severity -> chip style key (tamper = attention = purple). `high`
// gets the same treatment as `critical` — same operational weight.
const SEVERITY_CHIP_STYLE = {
  critical: CHIP_STYLES.error,
  high: CHIP_STYLES.error,
  warning: CHIP_STYLES.warning,
  attention: CHIP_STYLES.attention,
  info: CHIP_STYLES.info,
};

/**
 * Resolve the chip style for an alarm severity. Unknown / missing severity
 * falls back to the neutral default chip rather than red, so an absent
 * severity can't masquerade as critical.
 *
 * @param {string} severity
 * @returns {{ bgcolor: string, color: string }}
 */
export function severityChipStyle(severity) {
  return SEVERITY_CHIP_STYLE[severity] ?? CHIP_STYLES.default;
}

// Marker / status-dot colors for the map surfaces (CSS contexts only —
// Leaflet divIcon inline styles and sx both resolve the vars). Neutral means
// ODL has never heard from the site — no radio, or a radio that has never
// reported. Amber means it reported and went quiet, which is actionable in a
// way a site that was never commissioned is not.
export const MAP_STATUS_COLORS = {
  alarm: 'var(--status-bad)',
  offline: 'var(--status-warning)',
  ok: 'var(--status-ok)',
  silent: 'var(--status-neutral)',
};

// Stat-card gradient backgrounds, keyed by tone. Values resolve through
// the --stat-gradient-* tokens (theme/cssTokens.js), the single owner of
// the gradient hues.
export const GRADIENTS = {
  warning: 'var(--stat-gradient-warning)',
  error: 'var(--stat-gradient-error)',
  errorDeep: 'var(--stat-gradient-error-deep)',
  success: 'var(--stat-gradient-success)',
  info: 'var(--stat-gradient-info)',
  orange: 'var(--stat-gradient-orange)',
};
